import { putPlayItem, clearQueue as clearQueueApiCall } from "../controllers/SpotifyApiController";
import { getClearQueuePostOperation } from "../reducers/clearQueue";
import { getPointedDeviceId } from "../reducers/devices";
import { getVolume, supportsVolume } from "../reducers/playbackState";
import { getAlwaysClearQueue } from "../reducers/userSettings";
import { setClearingQueue, setPlaybackAction } from "./disablingProcessesActions";
import { resumeRandomMode, setFinishedAlbum, startRandomMode } from "./randomModeActions";
import { sortRecentlyPlayedAlbums } from "./albumListActions";
import { ITEM_TYPES } from "../utils/constants/enumConstants";
import { NOT_SET_VALUE } from "../utils/constants/userSettingsConstants";
import { manageTooManyRequestsResponse } from "../utils/connectionUtils";

export const POST_OPERATIONS = {
    playItem: 'playItem',
    startRandomMode: 'startRandomMode',
    resumeRandomMode: 'resumeRandomMode'
};

export const SET_CLEAR_QUEUE_POST_OPERATION = 'SET_CLEAR_QUEUE_POST_OPERATION';
export const SET_CLEAR_QUEUE_POPUP_SHOWING = 'SET_CLEAR_QUEUE_POPUP_SHOWING';

export const setClearQueuePostOperation = payload => ({ type: SET_CLEAR_QUEUE_POST_OPERATION, payload });
export const setClearQueuePopupShowing = payload => ({ type: SET_CLEAR_QUEUE_POPUP_SHOWING, payload });

// #region Pre clearing queue actions

const prepareClearQueue = (postOperation) => {
    return (dispatch, getState) => {
        if(!postOperation)
        {
            dispatch(setClearQueuePostOperation(undefined));
            dispatch(clearQueue());
            return;
        }

        const state = getState();
        const alwaysClearQueue = getAlwaysClearQueue(state);

        // The postOperation is registered
        dispatch(setClearQueuePostOperation(postOperation));

        // The user setting that indicates what to do in these cases is not set
        if(alwaysClearQueue === NOT_SET_VALUE) {
            // So, a popup is shown
            dispatch(setClearQueuePopupShowing(true));            
        }
        else if(alwaysClearQueue) {
            // The Spotify queue will be cleared and then, the postOperation will be executed
            dispatch(clearQueue());
        }
        else {
            // The postOperation is directly executed, without clearing the queue
            dispatch(executeClearQueuePostOperation(postOperation));
        }
    }
}

// To be used when it is wanted to just clear the Spotify queue, without a postOperation
export const setClearQueue = () => {
    return (dispatch) => {
        dispatch(prepareClearQueue());
    }
}

// To be used when, after the Spotify queue has been cleared, an album has to be played
export const setClearQueueForPlayAlbum = album => {
    return (dispatch) => {
        const postOperation = {
            type: POST_OPERATIONS.playItem,
            payload: {
                album
            }
        }

        dispatch(prepareClearQueue(postOperation));
    }
}

// To be used when, after the Spotify queue has been cleared, the random mode has to be started
export const setClearQueueForStartingRandomMode = ({fromCurrentTrack = false} = {}) => {
    return (dispatch) => {
        const postOperation = {
            type: POST_OPERATIONS.startRandomMode,
            payload: {
                fromCurrentTrack
            }
        }

        dispatch(prepareClearQueue(postOperation));
    }
}

// To be used when, after the Spotify queue has been cleared, the random mode has to be resumed
export const setClearQueueForResumingRandomMode = ({resumePausedAlbum = false} = {}) => {
    return (dispatch) => {
        const postOperation = {
            type: POST_OPERATIONS.resumeRandomMode,
            payload: {
                resumePausedAlbum
            }
        }

        dispatch(prepareClearQueue(postOperation));
    }
}

// #endregion Pre clearing queue actions

// #region Clearing queue actions

// Clears the Spotify queue and then executes the postOperation if there's any
export const clearQueue = () => {
    return (dispatch, getState) => {
        const state = getState();
        const pointedDeviceId = getPointedDeviceId(state);
        const originalVolume = getVolume(state);
        const canModifyVolume = supportsVolume(state);

        dispatch(setClearingQueue(true));

        clearQueueApiCall(pointedDeviceId, originalVolume, canModifyVolume)
            .then(() => {
                dispatch(setClearingQueue(false));

                dispatch(executeClearQueuePostOperation());
            })
            .catch((error) => {
                dispatch(setClearingQueue(false));
                manageTooManyRequestsResponse(error, dispatch);
                console.error('Error clearing queue: ', error);
            });
    }
}

// #endregion Clearing queue actions

// #region Post clearing queue actions

// Executes the postOperation that has been registered
export const executeClearQueuePostOperation = () => {
    return (dispatch, getState) => {
        const state = getState();
        const postOperation = getClearQueuePostOperation(state);

        if(!postOperation) return;

        switch(postOperation.type) {
            case POST_OPERATIONS.playItem:
                dispatch(playAlbumPostClearQueue(postOperation));
                break;
            case POST_OPERATIONS.startRandomMode:
                dispatch(startRandomModePostClearQueue(postOperation));
                break;
            case POST_OPERATIONS.resumeRandomMode:
                dispatch(resumeRandomModePostClearQueue(postOperation));
                break;
            default:
                break;
        }
    }
}

// Plays an album as the operation after clearing the Spotify queue
const playAlbumPostClearQueue = (postOperation) => {
    return (dispatch, getState) => {
        const { album } = postOperation && postOperation.payload;

        if(album === undefined)
        {
            return;
        }

        const state = getState();
        const pointedDeviceId = getPointedDeviceId(state);

        dispatch(setPlaybackAction(true));
        dispatch(setFinishedAlbum(false));

        putPlayItem(album.id, ITEM_TYPES.album, pointedDeviceId)
            .then(() => {
                dispatch(sortRecentlyPlayedAlbums(album));

                dispatch(setPlaybackAction(false));
            })
            .catch((error) => {
                dispatch(setPlaybackAction(false));
                manageTooManyRequestsResponse(error, dispatch);
                console.error(`Error playing album ${album.id} ${album.name}: `, error);
            });
    }
}

// Starts the random mode as the operation after clearing the Spotify queue
const startRandomModePostClearQueue = (postOperation) => {
    return (dispatch) => {
        const { fromCurrentTrack } = postOperation && postOperation.payload;

        if(fromCurrentTrack === undefined)
        {
            return;
        }

        dispatch(startRandomMode({ fromCurrentTrack }));
    }
}

// Resumes the random mode as the operation after clearing the Spotify queue
const resumeRandomModePostClearQueue = (postOperation) => {
    return (dispatch) => {
        const { resumePausedAlbum } = postOperation && postOperation.payload;

        if(resumePausedAlbum === undefined)
        {
            return;
        }

        dispatch(resumeRandomMode({ resumePausedAlbum }));
    }
}

// #endregion Post clearing queue actions